import { type ComponentType, lazy } from 'react';
import { webBuildInfo } from './buildInfo';

/**
 * Wrap a lazy import so that a "failed to fetch dynamically imported module"
 * error — the classic symptom of a stale client bundle referencing a chunk
 * whose hashed filename no longer exists after a deploy — recovers with a
 * single full page reload. The reload picks up the new index.html and its
 * matching chunk hashes, instead of leaving the user on the error boundary.
 *
 * Allow one reload per frontend revision. A successful parent import must not
 * reset the budget of a failing child; a newer bundle gets its own attempt.
 *
 * @see https://vite.dev/guide/build#load-error-handling
 */
const STALE_CHUNK_KEY = 'cabgo.lazy.retry';

export function lazyImport<C extends ComponentType<unknown>>(factory: () => Promise<{ default: C }>) {
  return lazy(() =>
    factory().catch((err: unknown) => {
      const isStaleChunk =
        err instanceof Error &&
        /Failed to fetch dynamically imported module|Importing a module script failed/i.test(err.message);
      const revision = webBuildInfo.revision || 'local';
      if (isStaleChunk && sessionStorage.getItem(STALE_CHUNK_KEY) !== revision) {
        sessionStorage.setItem(STALE_CHUNK_KEY, revision);
        window.location.reload();
        // The reload unloads this context; keep the promise pending so the
        // Suspense fallback stays visible rather than re-throwing.
        return new Promise<{ default: C }>(() => {});
      }
      throw err;
    }),
  );
}
